import { createFileRoute, Link, useNavigate, Outlet, useRouterState } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { Button } from '../components/ui/Button';
import { DeleteGigModal } from '../components/gigs/DeleteGigModal';
import { useGetGig, useGetGigs, useGetPublicGig } from '../api/queries';
import { useDeleteGig } from '../api/mutations';
import {
  FiArrowLeft,
  FiBriefcase,
  FiShare2,
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiAlertCircle,
  FiNavigation,
  FiChevronDown,
  FiChevronUp,
  FiUser,
  FiCalendar,
  FiGlobe,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
} from 'react-icons/fi';
import { useState, useEffect, useRef, type MouseEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { GigCard } from '../components/gigs/GigCard';
import { ApplyGigModal, type ApplyGigData } from '../components/gigs/ApplyGigModal';
import { copyTextToClipboard } from '../utils/copyToClipboard';
import {
  getGigClosingTimeLabel,
  getGigStatusPresentation,
  isGigActive,
  isApplicationOpen,
  isApplicationNotYetOpen,
  canUserApplyToGig,
  readCanApplyFlag,
} from '../utils/gigDate';
import { toast } from '../utils/toast';
import { normalizeGigTagLabels } from '../utils/gigTags';
import { getProfileIdentifier } from '../utils/profileUrl';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { useTimezoneLabel } from '../api/timezones';

export const Route = createFileRoute('/gigs/$gigId')({
  validateSearch: (search: Record<string, unknown>) => ({
    apply: String(search.apply || '') === '1' || String(search.apply || '') === 'true',
  }),
  component: GigDetail,
});

function formatPostedDate(isoString?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  const day = d.getDate();
  const v = day % 100;
  const suffix =
    v >= 11 && v <= 13
      ? 'th'
      : day % 10 === 1
        ? 'st'
        : day % 10 === 2
          ? 'nd'
          : day % 10 === 3
            ? 'rd'
            : 'th';
  const month = d.toLocaleDateString('en-US', { month: 'long' });
  const year = d.getFullYear();
  const time = d
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toLowerCase();
  return `${day}${suffix} ${month} ${year} • ${time}`;
}

/** Gig times are stored "as picked" (UTC-stamped), so they are read back in UTC. */
function formatDateTime(isoString?: string): string {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }).format(date);
}

function formatMemberSince(isoString?: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(d);
}

function formatBudget(gig: {
  budget_type?: string;
  budget_amount?: string | null;
  budget_min?: string | null;
  budget_max?: string | null;
}): string | null {
  if (gig.budget_type === 'flat_rate') {
    const amount = gig.budget_amount ?? gig.budget_min ?? gig.budget_max;
    if (amount) return `$${parseFloat(amount).toLocaleString()}`;
  }
  if (gig.budget_type === 'range') {
    const min = gig.budget_min ? `$${parseFloat(gig.budget_min).toLocaleString()}` : null;
    const max = gig.budget_max ? `$${parseFloat(gig.budget_max).toLocaleString()}` : null;
    if (min && max && min !== max) return `${min} – ${max}`;
    if (min) return `From ${min}`;
    if (max) return `Up to ${max}`;
  }
  return null;
}

const PLACEHOLDER_GRADIENTS = [
  'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
  'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)',
  'linear-gradient(135deg, #3c1053 0%, #ad5389 100%)',
  'linear-gradient(135deg, #1a1a1a 0%, #4a0000 100%)',
  'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
];

const GIG_IMAGE_FALLBACK_SRC = '/Musicade-Logo.png';

type GigImageProps = {
  src: string;
  alt: string;
  className: string;
  fallbackClassName?: string;
  onClick?: (e: MouseEvent<HTMLImageElement>) => void;
  onSourceError?: () => void;
  draggable?: boolean;
};

function GigImage({
  src,
  alt,
  className,
  fallbackClassName = 'w-full h-full object-contain p-6',
  onClick,
  onSourceError,
  draggable,
}: GigImageProps) {
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    setHasFailed(false);
  }, [src]);

  return (
    <img
      src={hasFailed || !src.trim() ? GIG_IMAGE_FALLBACK_SRC : src}
      alt={hasFailed ? `${alt} fallback` : alt}
      className={hasFailed ? fallbackClassName : className}
      onClick={onClick}
      draggable={draggable}
      onError={() => {
        if (!hasFailed) {
          setHasFailed(true);
          onSourceError?.();
        }
      }}
    />
  );
}

function getToneClass(tone: 'positive' | 'negative' | 'neutral'): string {
  if (tone === 'positive') return 'bg-green-500/10 border border-green-500/30 text-green-400';
  if (tone === 'negative') return 'bg-red-500/10 border border-red-500/30 text-red-400';
  return 'bg-zinc-800 border border-white/10 text-zinc-300';
}

function GigDetail() {
  const getTimezoneLabel = useTimezoneLabel();
  const { gigId } = Route.useParams();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { apply } = Route.useSearch();
  const { user, isAuthenticated } = useAuth();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [applyTarget, setApplyTarget] = useState<{
    gig: ApplyGigData;
    gigId: string;
    ownerEmail?: string;
  } | null>(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [hasHeroImageError, setHasHeroImageError] = useState(false);
  const [hasOwnerImageError, setHasOwnerImageError] = useState(false);
  const hasAutoTriggeredApply = useRef(false);

  // Always call both hooks; only one fetches based on the enabled flag
  const authGigResult = useGetGig(isAuthenticated ? gigId : '');
  const publicGigResult = useGetPublicGig(!isAuthenticated ? gigId : undefined);
  const { data: gig, isLoading, isError } = isAuthenticated ? authGigResult : publicGigResult;
  const { mutate: deleteGig, isPending: isDeleting } = useDeleteGig();
  const { data: relatedData } = useGetGigs({
    event_type: gig?.event_type || undefined,
    page_size: 4,
  });
  const gigAny = (gig ?? {}) as Record<string, unknown>;
  const mediaImageUrls = Array.isArray(gigAny.medias)
    ? gigAny.medias
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return '';
          const record = entry as Record<string, unknown>;
          const mediaType = String(record.media_type || '').toLowerCase();
          const url = String(record.file_url || record.url || '');
          if (!url) return '';
          if (mediaType && mediaType !== 'image') return '';
          return url.trim();
        })
        .filter(Boolean)
    : [];
  const gigImages = Array.from(
    new Set([String(gig?.image || '').trim(), ...mediaImageUrls]),
  ).filter(Boolean);

  // Auto-open apply modal when navigated with ?apply=1 (e.g. from home page)
  // Must be declared before any early returns to satisfy rules-of-hooks
  useEffect(() => {
    if (apply && gig && !isLoading && !hasAutoTriggeredApply.current) {
      hasAutoTriggeredApply.current = true;
      if (readCanApplyFlag(gig) === false) {
        toast.error('Applications for this gig are currently closed.');
        return;
      }
      if (!isGigActive(gig)) {
        toast.error('This gig has expired and can no longer accept applications.');
        return;
      }

      if (!isAuthenticated) {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('apply', '1');
        navigate({
          to: '/sign-in',
          search: { redirect: window.location.pathname + '?' + searchParams.toString() },
        });
        return;
      }

      setApplyTarget({
        gig: gig as unknown as ApplyGigData,
        gigId,
      });
      setIsApplyModalOpen(true);
    }
  }, [apply, gig, isLoading, isAuthenticated, navigate, gigId]);

  useEffect(() => {
    if (routerState.location.pathname.endsWith('/edit')) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [gigId, routerState.location.pathname]);

  const heroImage = gigImages[0];
  useEffect(() => {
    setHasHeroImageError(false);
  }, [heroImage]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handler = (event: KeyboardEvent) => {
      const total = gigImages.length;
      if (event.key === 'Escape') setLightboxIndex(null);
      if (event.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev !== null && prev < total - 1 ? prev + 1 : prev));
      }
      if (event.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev !== null && prev > 0 ? prev - 1 : prev));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxIndex, gigImages.length]);

  if (routerState.location.pathname.endsWith('/edit')) {
    return <Outlet />;
  }

  const handleDelete = () => {
    deleteGig(gigId, {
      onSuccess: () => {
        setIsDeleteModalOpen(false);
        navigate({ to: '/gigs' });
      },
      onError: () => setIsDeleteModalOpen(false),
    });
  };

  const handleOpenApply = (
    targetGig?: ApplyGigData,
    targetGigId?: string,
    targetOwnerEmail?: string,
  ) => {
    if (!gig && !targetGig) return;

    const gigToApply = targetGig ?? (gig as unknown as ApplyGigData);
    if (readCanApplyFlag(gigToApply) === false) {
      toast.error('Applications for this gig are currently closed.');
      return;
    }

    if (!isGigActive(gigToApply)) {
      toast.error('This gig has expired and can no longer accept applications.');
      return;
    }

    if (!isApplicationOpen(gigToApply)) {
      toast.error(
        isApplicationNotYetOpen(gigToApply)
          ? 'Applications for this gig are not open yet.'
          : 'Applications for this gig are closed.',
      );
      return;
    }

    if (!isAuthenticated) {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('apply', '1');
      navigate({
        to: '/sign-in',
        search: { redirect: window.location.pathname + '?' + searchParams.toString() },
      });
      return;
    }

    setApplyTarget({
      gig: gigToApply,
      gigId: targetGigId ?? gigId,
      ownerEmail: targetOwnerEmail ?? ownerEmail,
    });
    setIsApplyModalOpen(true);
  };

  const handleShare = async () => {
    const url = window.location.href;
    const copied = await copyTextToClipboard(url);

    if (copied) {
      toast.success('Link copied');
      return;
    }

    toast.error('Unable to copy link. Please copy manually.');
  };

  if (isLoading) {
    return <LoadingLogo />;
  }

  if (isError || !gig) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Gig not found</h2>
          <p className="text-zinc-500 mb-6">
            This gig may have been removed or you don't have access.
          </p>
          <Button onClick={() => navigate({ to: '/gigs' })} size="lg" className="rounded-full px-8">
            Back to Gigs
          </Button>
        </main>
        <BottomNav />
      </div>
    );
  }

  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const budgetDisplay = formatBudget(gig);
  // API returns created_at and gig_uuid
  const postedDate = formatPostedDate(
    (gig as Record<string, unknown>).created_at as string | undefined,
  );
  const gradientIndex =
    String((gig as Record<string, unknown>).gig_uuid || gigId).charCodeAt(0) %
    PLACEHOLDER_GRADIENTS.length;

  const owner = (gigAny.owner ?? {}) as Record<string, unknown>;
  const venue = (gigAny.venue ?? {}) as Record<string, unknown>;
  const isPremium = !!(gigAny.is_premium || gigAny.tier === 'premium');

  // Ownership: API returns owner.id, not a top-level owner_id
  const ownerUserId = String(owner.id || '');
  const isOwner = !!currentUserId && !!ownerUserId && ownerUserId === String(currentUserId);
  const showInternalStatus = isOwner;
  const ownerStatusPresentation = getGigStatusPresentation(gig, true);
  const publicStatusPresentation = getGigStatusPresentation(gig, false);

  const ownerName =
    String(owner.full_name || owner.name || owner.username || owner.display_name || '').trim() ||
    undefined;
  const ownerId = owner.id ?? gigAny.owner_id;
  const ownerIdentifier =
    String(
      getProfileIdentifier({
        id: ownerId as string | number | null,
        username: owner.username as string | null,
      }),
    ).trim() || undefined;
  const ownerDisplayName =
    ownerName ||
    (ownerId !== undefined && ownerId !== null && String(ownerId).trim()
      ? `User #${String(ownerId).trim()}`
      : undefined);
  const ownerCompany =
    String(owner.company_name || owner.company || owner.business_name || '').trim() || undefined;
  const ownerEmail = String(owner.email || '').trim() || undefined;
  const ownerImage =
    String(
      owner.profile_picture || owner.profile_image || owner.avatar || owner.image || '',
    ).trim() || undefined;
  const ownerMemberSince = formatMemberSince(
    String(owner.date_joined || owner.created_at || owner.created || owner.member_since || ''),
  );
  const ownerLocation =
    String(owner.location || owner.city || '').trim() ||
    [gig.location?.city, gig.location?.state].filter(Boolean).join(', ') ||
    undefined;

  const venueName = String(venue.name || gig.location_name || '').trim() || undefined;
  const venueStageSize = String(venue.stage_size || '').trim() || undefined;
  const venueCapacity = venue.capacity ? `${venue.capacity} guests` : undefined;
  const venueFacilities = String(venue.facilities || '').trim() || undefined;
  const venueAddress =
    String(venue.address || venue.full_address || '').trim() || gig.location?.street || undefined;

  const hasOwnerInfo = !!(
    ownerDisplayName ||
    ownerCompany ||
    ownerImage ||
    ownerLocation ||
    ownerMemberSince ||
    ownerIdentifier
  );
  const hasVenueInfo = !!(
    venueName ||
    venueStageSize ||
    venueCapacity ||
    venueFacilities ||
    venueAddress
  );

  type AnyGig = Record<string, unknown>;
  const gigExpired = !isGigActive(gig);
  const appOpen = isApplicationOpen(gig);
  const applicationNotYetOpen = !gigExpired && isApplicationNotYetOpen(gig);
  const canApplyToCurrentGig = canUserApplyToGig(gig);
  const applicationClosed = !gigExpired && !appOpen && !applicationNotYetOpen;
  // The backend can close applications while the dates still look open.
  const applyClosed =
    applicationClosed ||
    (!canApplyToCurrentGig && !gigExpired && !applicationNotYetOpen && !applicationClosed);
  const closingLabel = canApplyToCurrentGig ? getGigClosingTimeLabel(gig) : null;
  const relatedGigs = ((relatedData?.results ?? []) as AnyGig[])
    .filter((g) => String(g.id || g.gig_uuid) !== String(gigId))
    .filter((g) => isGigActive(g))
    .slice(0, 3);

  const handleGetDirection = () => {
    const address =
      venueAddress ||
      venueName ||
      gig.location_name ||
      [gig.location?.city, gig.location?.state, gig.location?.country].filter(Boolean).join(', ');
    if (address) window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-32 pt-20 lg:pt-[calc(var(--header-height)+1.5rem)]">
        {/* Breadcrumb / top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-5">
          <div className="flex items-center gap-2 text-zinc-500 text-[13px] overflow-x-auto no-scrollbar whitespace-nowrap">
            <button
              onClick={() => window.history.back()}
              className="hover:text-white transition-colors shrink-0 p-1 -ml-1 rounded-full hover:bg-white/5"
            >
              <FiArrowLeft size={20} />
            </button>
            <FiBriefcase size={14} className="shrink-0" />
            <Link to="/gigs" className="hover:text-white transition-colors font-medium shrink-0">
              Gig Discovery
            </Link>
            <span className="text-zinc-700 shrink-0">&rsaquo;</span>
            <span className="text-zinc-400 truncate max-w-[40vw] sm:max-w-xs">{gig.title}</span>
          </div>
          {/* Owner controls: show Edit+Delete only if the gig is still active */}
          {isOwner && !gigExpired && (
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/gigs/$gigId/edit" params={{ gigId }} search={{ apply: false }}>
                <Button
                  variant="secondary"
                  className="rounded-full gap-2 px-5 bg-zinc-900 border-white/5 hover:bg-white/5"
                >
                  <FiEdit2 size={14} />
                  <span>Edit</span>
                </Button>
              </Link>
              <Button
                variant="secondary"
                className="rounded-full gap-2 px-5 bg-zinc-900 border-white/5 text-red-500 hover:bg-red-500/10"
                onClick={() => setIsDeleteModalOpen(true)}
              >
                <FiTrash2 size={14} />
                <span>Delete</span>
              </Button>
            </div>
          )}
          {/* Expired notice: visible to all users when the gig has passed its end date */}
          {gigExpired && (
            <div className="flex items-center gap-2 shrink-0 bg-red-950/60 border border-red-800/50 rounded-full px-4 py-2">
              <FiClock size={13} className="text-red-400 shrink-0" />
              <span className="text-red-400 text-[13px] font-semibold">This gig has expired</span>
            </div>
          )}
        </div>

        {/* Hero Banner (mobile/tablet only) */}
        <div className="relative overflow-hidden lg:hidden -mx-4 sm:-mx-6">
          <div className="w-full aspect-video overflow-hidden bg-zinc-950">
            {gigImages.length > 0 ? (
              <button
                type="button"
                className="w-full h-full block cursor-zoom-in"
                onClick={() => setLightboxIndex(0)}
              >
                <GigImage
                  src={gigImages[0]}
                  alt={gig.title}
                  className="w-full h-full object-contain"
                  fallbackClassName="w-full h-full object-contain p-8 bg-zinc-950"
                  onSourceError={() => setHasHeroImageError(true)}
                />
              </button>
            ) : (
              <div
                className="w-full h-full"
                style={{ background: PLACEHOLDER_GRADIENTS[gradientIndex] }}
              />
            )}
            <div className="absolute inset-0 pointer-events-none bg-linear-to-t from-black/80 via-black/20 to-transparent" />
            {hasHeroImageError && (
              <div className="absolute right-4 bottom-4 bg-black/70 border border-white/10 text-zinc-200 text-xs px-3 py-1.5 rounded-full">
                Unable to load image.
              </div>
            )}
          </div>
        </div>

        {/* Two-column grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* ── LEFT COLUMN ── */}
          <div className="lg:col-span-2 space-y-8">
            {/* Desktop image panel */}
            <div className="hidden lg:block">
              <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-zinc-950 border border-white/8">
                {gigImages.length > 0 ? (
                  <button
                    type="button"
                    className="w-full h-full block cursor-zoom-in"
                    onClick={() => setLightboxIndex(0)}
                  >
                    <GigImage
                      src={gigImages[0]}
                      alt={gig.title}
                      className="w-full h-full object-contain"
                      fallbackClassName="w-full h-full object-contain p-10 bg-zinc-950"
                      onSourceError={() => setHasHeroImageError(true)}
                    />
                  </button>
                ) : (
                  <div
                    className="w-full h-full"
                    style={{ background: PLACEHOLDER_GRADIENTS[gradientIndex] }}
                  />
                )}
                <div className="absolute inset-0 pointer-events-none bg-linear-to-t from-black/70 via-black/10 to-transparent" />
                {hasHeroImageError && (
                  <div className="absolute right-4 bottom-4 bg-black/70 border border-white/10 text-zinc-200 text-xs px-3 py-1.5 rounded-full">
                    Unable to load image.
                  </div>
                )}
              </div>
            </div>

            {/* Title block */}
            <div className="space-y-3">
              {(isPremium || showInternalStatus) && (
                <div className="flex flex-wrap items-center gap-2">
                  {isPremium && (
                    <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest bg-blue-900/60 text-blue-300 border border-blue-500/30">
                      Premium
                    </span>
                  )}
                  {showInternalStatus && !gigExpired && (
                    <span
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold ${getToneClass(ownerStatusPresentation.tone)}`}
                    >
                      {ownerStatusPresentation.label}
                    </span>
                  )}
                  {!showInternalStatus && !gigExpired && (
                    <span
                      className={`px-3 py-1 rounded-full text-[11px] font-semibold ${getToneClass(publicStatusPresentation.tone)}`}
                    >
                      {publicStatusPresentation.label}
                    </span>
                  )}
                </div>
              )}

              {postedDate && (
                <div className="flex items-center gap-1.5 text-zinc-500 text-[13px]">
                  <FiCalendar size={13} />
                  <span>
                    Posted: <span className="text-zinc-400">{postedDate}</span>
                  </span>
                </div>
              )}

              <h1 className="text-white text-2xl sm:text-3xl lg:text-[38px] font-bold leading-tight uppercase tracking-tight">
                {gig.title}
              </h1>

              <div className="flex flex-wrap gap-2 pt-1">
                {budgetDisplay && (
                  <div className="flex items-center gap-1.5 text-[13px] text-violet-400 font-medium bg-violet-500/10 border border-violet-500/20 rounded-full px-3 py-1">
                    <span>
                      {gig.budget_type === 'flat_rate' ? 'Flat Rate: ' : 'Budget: '}
                      {budgetDisplay}
                    </span>
                  </div>
                )}
                {gig.event_type && (
                  <span className="px-3 py-1 rounded-full text-[12px] font-medium bg-zinc-800 border border-white/10 text-zinc-300 capitalize">
                    {gig.event_type}
                  </span>
                )}
                {gig.start_datetime && (
                  <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 bg-zinc-900/60 border border-white/5 rounded-full px-3 py-1">
                    <FiCalendar size={11} />
                    <span className="text-zinc-500 mr-1">Start:</span>
                    {formatDateTime(gig.start_datetime)}
                  </div>
                )}
                {gig.end_datetime && (
                  <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 bg-zinc-900/60 border border-white/5 rounded-full px-3 py-1">
                    <FiCalendar size={11} />
                    <span className="text-zinc-500 mr-1">End:</span>
                    {formatDateTime(gig.end_datetime)}
                  </div>
                )}
                {isOwner && (gig as Record<string, unknown>).application_start_datetime && (
                  <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 bg-zinc-900/60 border border-white/5 rounded-full px-3 py-1">
                    <FiClock size={11} />
                    <span className="text-zinc-500 mr-1">Apply From:</span>
                    {formatDateTime(
                      (gig as Record<string, unknown>).application_start_datetime as string,
                    )}
                  </div>
                )}
                {isOwner && (gig as Record<string, unknown>).application_end_datetime && (
                  <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 bg-zinc-900/60 border border-white/5 rounded-full px-3 py-1">
                    <FiClock size={11} />
                    <span className="text-zinc-500 mr-1">Apply Until:</span>
                    {formatDateTime(
                      (gig as Record<string, unknown>).application_end_datetime as string,
                    )}
                  </div>
                )}
                {(() => {
                  const tzLabel = getTimezoneLabel(
                    (gig as Record<string, unknown>).timezone as string,
                  );
                  return tzLabel ? (
                    <div className="flex items-center gap-1.5 text-[12px] text-zinc-400 bg-zinc-900/60 border border-white/5 rounded-full px-3 py-1">
                      <FiGlobe size={11} />
                      <span className="text-zinc-500 mr-1">Timezone:</span>
                      {tzLabel}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>

            <div className="w-full h-px bg-white/5" />

            {/* Description */}
            {gig.description && (
              <div>
                <h2 className="text-white text-xl font-semibold mb-4">Full Description</h2>
                <p className="text-zinc-400 text-[15px] leading-relaxed whitespace-pre-line">
                  {gig.description}
                </p>
              </div>
            )}

            {/* Tags */}
            {(() => {
              const gigTags = normalizeGigTagLabels(gigAny.tags);
              return gigTags.length > 0 ? (
                <div>
                  <h2 className="text-white text-xl font-semibold mb-4">Tags</h2>
                  <div className="flex flex-wrap gap-2">
                    {gigTags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full bg-zinc-800 border border-white/8 text-zinc-300 text-[12px] font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Additional Info */}
            {gig.additional_information && (
              <div>
                <h2 className="text-white text-xl font-semibold mb-4">Additional Information</h2>
                <p className="text-zinc-400 text-[15px] leading-relaxed whitespace-pre-line">
                  {gig.additional_information}
                </p>
              </div>
            )}

            {/* Location fallback (if no venue data) */}
            {!hasVenueInfo && (gig.location_name || gig.location) && (
              <div className="flex items-start gap-3 p-4 bg-zinc-900/40 border border-white/5 rounded-2xl">
                <FiMapPin className="text-blue-500 mt-0.5 shrink-0" size={16} />
                <div>
                  <p className="text-zinc-500 text-[12px] uppercase tracking-wider font-medium mb-0.5">
                    Location
                  </p>
                  <p className="text-white font-medium text-[15px]">
                    {gig.location_name ||
                      [gig.location?.city, gig.location?.state, gig.location?.country]
                        .filter(Boolean)
                        .join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Share */}
            <div className="pt-2">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 h-11 px-7 rounded-full border border-white/15 text-zinc-300 text-[14px] font-medium hover:bg-white/5 hover:border-white/25 transition-all uppercase tracking-wider"
              >
                <FiShare2 size={15} />
                Copy Link
              </button>
            </div>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-[calc(var(--header-height)+1rem)] lg:max-h-[calc(100vh-var(--header-height)-2rem)] lg:overflow-y-auto no-scrollbar space-y-4">
              {/* Client card */}
              <div className="bg-zinc-950 border border-white/8 rounded-3xl overflow-hidden">
                {/* Profile image */}
                <div className="w-full h-48 bg-zinc-800/80 overflow-hidden">
                  {ownerImage && !hasOwnerImageError ? (
                    ownerIdentifier ? (
                      <Link
                        to="/profile/$identifier"
                        params={{ identifier: ownerIdentifier }}
                        className="block w-full h-full"
                        aria-label="View gig owner profile"
                      >
                        <img
                          src={ownerImage}
                          alt={ownerDisplayName ?? 'Gig owner'}
                          className="w-full h-full object-cover"
                          onError={() => setHasOwnerImageError(true)}
                        />
                      </Link>
                    ) : (
                      <img
                        src={ownerImage}
                        alt={ownerDisplayName ?? 'Gig owner'}
                        className="w-full h-full object-cover"
                        onError={() => setHasOwnerImageError(true)}
                      />
                    )
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FiUser size={44} className="text-zinc-600" />
                    </div>
                  )}
                </div>

                {/* Client info */}
                {hasOwnerInfo && (
                  <div className="px-5 pt-5 pb-2">
                    <p className="text-white text-[10px] font-bold uppercase tracking-widest mb-4">
                      Information About The Client
                    </p>
                    <div className="space-y-3">
                      {(ownerCompany || ownerName) && (
                        <div>
                          <p className="text-zinc-500 text-[12px] font-medium">
                            {ownerCompany ? 'Company' : 'Name'}
                          </p>
                          <p className="text-white text-[14px] font-medium mt-0.5">
                            {ownerCompany ?? ownerName}
                          </p>
                        </div>
                      )}
                      {ownerMemberSince && (
                        <div>
                          <p className="text-zinc-500 text-[12px] font-medium">Member Since</p>
                          <p className="text-white text-[14px] font-medium mt-0.5">
                            {ownerMemberSince}
                          </p>
                        </div>
                      )}
                      {ownerLocation && (
                        <div>
                          <p className="text-zinc-500 text-[12px] font-medium">Location</p>
                          <p className="text-white text-[14px] font-medium mt-0.5">
                            {ownerLocation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Apply / heart */}
                {!isOwner && (
                  <div className="px-5 pt-4 pb-2 flex items-start gap-3">
                    <div className="flex-1">
                      <button
                        onClick={() => handleOpenApply()}
                        disabled={!canApplyToCurrentGig}
                        className={`w-full h-11 rounded-2xl text-white text-[12px] font-bold uppercase tracking-widest transition-all ${
                          canApplyToCurrentGig
                            ? 'bg-blue-600 hover:bg-blue-500 active:scale-95'
                            : applicationNotYetOpen
                              ? 'bg-amber-900/40 text-amber-400 cursor-not-allowed border border-amber-700/40'
                              : applyClosed
                                ? 'bg-red-900/40 text-red-400 cursor-not-allowed border border-red-700/40'
                                : 'bg-zinc-700/70 text-zinc-300 cursor-not-allowed'
                        }`}
                      >
                        {canApplyToCurrentGig
                          ? 'Apply to Gig'
                          : applicationNotYetOpen
                            ? 'Not Yet Open'
                            : applyClosed
                              ? 'Application Closed'
                              : 'Gig Expired'}
                      </button>
                      {closingLabel && (
                        <p className="mt-1 text-[11px] text-zinc-500">{closingLabel}</p>
                      )}
                    </div>
                    {/* <button
                      onClick={() => setLiked((v) => !v)}
                      className={`w-11 h-11 rounded-2xl border flex items-center justify-center transition-all ${
                        liked
                          ? 'bg-pink-600/20 border-pink-500/40 text-pink-500'
                          : 'bg-zinc-900 border-white/10 text-zinc-400 hover:text-pink-400 hover:border-pink-500/30'
                      }`}
                    >
                      <FiHeart size={17} fill={liked ? 'currentColor' : 'none'} />
                    </button> */}
                  </div>
                )}

                {/* Show more/less toggle */}
                <button
                  onClick={() => setSidebarExpanded((v) => !v)}
                  className="w-full flex items-center justify-center gap-1.5 py-3 border-t border-white/5 mt-2 text-yellow-400 text-[13px] font-medium hover:text-yellow-300 transition-colors"
                >
                  {sidebarExpanded ? (
                    <>
                      Show less <FiChevronUp size={14} />
                    </>
                  ) : (
                    <>
                      Show more <FiChevronDown size={14} />
                    </>
                  )}
                </button>
              </div>

              {/* Venue Details card (expanded) */}
              {sidebarExpanded && (
                <div className="bg-zinc-950 border border-white/8 rounded-3xl overflow-hidden">
                  <div className="flex items-center gap-2.5 px-5 pt-5 pb-4 border-b border-white/5">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <FiMapPin size={14} className="text-zinc-400" />
                    </div>
                    <p className="text-white text-[10px] font-bold uppercase tracking-widest">
                      Venue Details
                    </p>
                  </div>
                  <div className="px-5 py-4 space-y-3">
                    {venueName && (
                      <div>
                        <p className="text-zinc-500 text-[12px] font-medium">Name</p>
                        <p className="text-white text-[14px] font-medium mt-0.5">{venueName}</p>
                      </div>
                    )}
                    {venueStageSize && (
                      <div>
                        <p className="text-zinc-500 text-[12px] font-medium">Stage Size</p>
                        <p className="text-white text-[14px] font-medium mt-0.5">
                          {venueStageSize}
                        </p>
                      </div>
                    )}
                    {venueCapacity && (
                      <div>
                        <p className="text-zinc-500 text-[12px] font-medium">Capacity</p>
                        <p className="text-white text-[14px] font-medium mt-0.5">{venueCapacity}</p>
                      </div>
                    )}
                    {venueFacilities && (
                      <div>
                        <p className="text-zinc-500 text-[12px] font-medium">Facilities</p>
                        <p className="text-white text-[14px] font-medium mt-0.5">
                          {venueFacilities}
                        </p>
                      </div>
                    )}
                    {venueAddress && (
                      <div>
                        <p className="text-zinc-500 text-[12px] font-medium">Address</p>
                        <p className="text-white text-[14px] font-medium mt-0.5">{venueAddress}</p>
                      </div>
                    )}
                    {(venueAddress || venueName) && (
                      <div className="w-full h-36 rounded-2xl bg-zinc-800/60 border border-white/5 overflow-hidden mt-1">
                        <iframe
                          title="venue-map"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          loading="lazy"
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(venueAddress || venueName || '')}&output=embed`}
                        />
                      </div>
                    )}
                    <button
                      onClick={handleGetDirection}
                      className="w-full flex items-center justify-center gap-2 h-11 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-[12px] font-bold uppercase tracking-widest transition-all active:scale-95"
                    >
                      Get Direction <FiNavigation size={13} />
                    </button>
                    <button
                      onClick={() => setSidebarExpanded(false)}
                      className="w-full flex items-center justify-center gap-1.5 pt-1 text-yellow-400 text-[13px] font-medium hover:text-yellow-300 transition-colors"
                    >
                      Show less <FiChevronUp size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* Gig summary card */}
              <div className="bg-zinc-950 border border-white/8 rounded-2xl px-5 py-4">
                <p className="text-white text-[10px] font-bold uppercase tracking-widest mb-3">
                  Gig Details
                </p>
                <div className="space-y-2.5 text-[13px]">
                  {budgetDisplay && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Budget</span>
                      <span className="text-white font-medium">{budgetDisplay}</span>
                    </div>
                  )}
                  {gig.start_datetime && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">Start</span>
                      <span className="text-white font-medium text-right">
                        {formatDateTime(gig.start_datetime)}
                      </span>
                    </div>
                  )}
                  {gig.end_datetime && (
                    <div className="flex justify-between">
                      <span className="text-zinc-500">End</span>
                      <span className="text-white font-medium text-right">
                        {formatDateTime(gig.end_datetime)}
                      </span>
                    </div>
                  )}
                  {isOwner && (gig as Record<string, unknown>).application_start_datetime && (
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-zinc-500 shrink-0">Apply From</span>
                      <span className="text-white font-medium text-right">
                        {formatDateTime(
                          (gig as Record<string, unknown>).application_start_datetime as string,
                        )}
                      </span>
                    </div>
                  )}
                  {isOwner && (gig as Record<string, unknown>).application_end_datetime && (
                    <div className="flex justify-between items-start gap-4">
                      <span className="text-zinc-500 shrink-0">Apply Until</span>
                      <span className="text-white font-medium text-right">
                        {formatDateTime(
                          (gig as Record<string, unknown>).application_end_datetime as string,
                        )}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const tzLabel = getTimezoneLabel(
                      (gig as Record<string, unknown>).timezone as string,
                    );
                    return tzLabel ? (
                      <div className="flex justify-between items-start gap-4">
                        <span className="text-zinc-500 shrink-0">Timezone</span>
                        <span className="text-white font-medium text-right text-[12px] leading-snug">
                          {tzLabel}
                        </span>
                      </div>
                    ) : null;
                  })()}
                  {showInternalStatus && (
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-500">Status</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getToneClass(ownerStatusPresentation.tone)}`}
                      >
                        {ownerStatusPresentation.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* More Like This */}
        {relatedGigs.length > 0 && (
          <div className="mt-16 pt-12 border-t border-white/5">
            <h2 className="text-white text-2xl font-bold mb-6 uppercase tracking-tight">
              More Like This
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedGigs.map((g) => (
                <GigCard
                  key={String(g.id || g.gig_uuid)}
                  gig={g as unknown as Parameters<typeof GigCard>[0]['gig']}
                  onViewDetails={(item) =>
                    navigate({
                      to: '/gigs/$gigId',
                      params: { gigId: String(item.gig_uuid || item.id) },
                      search: { apply: false },
                    })
                  }
                  onApply={(item) =>
                    handleOpenApply(
                      item as unknown as ApplyGigData,
                      String(item.gig_uuid || item.id),
                      undefined,
                    )
                  }
                />
              ))}
            </div>
          </div>
        )}
      </main>

      <BottomNav />

      {/* Delete modal */}
      {isDeleteModalOpen && (
        <DeleteGigModal
          gigTitle={gig.title}
          isDeleting={isDeleting}
          onConfirm={handleDelete}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}

      {/* Apply modal */}
      <ApplyGigModal
        isOpen={isApplyModalOpen}
        onClose={() => {
          setIsApplyModalOpen(false);
          setApplyTarget(null);
        }}
        gig={applyTarget?.gig ?? (gig as unknown as ApplyGigData)}
        gigId={applyTarget?.gigId ?? gigId}
        user={user}
      />

      {lightboxIndex !== null && gigImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-100 bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center p-4 sm:p-8"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            onClick={(event) => {
              event.stopPropagation();
              setLightboxIndex(null);
            }}
          >
            <FiX size={20} />
          </button>

          {lightboxIndex > 0 && (
            <button
              className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((prev) => (prev !== null ? prev - 1 : prev));
              }}
            >
              <FiChevronLeft size={24} />
            </button>
          )}

          <GigImage
            src={gigImages[lightboxIndex]}
            alt={`${gig.title} image ${lightboxIndex + 1}`}
            className="max-w-[88vw] max-h-[75vh] object-contain rounded-2xl shadow-2xl"
            fallbackClassName="max-w-[88vw] max-h-[75vh] object-contain rounded-2xl shadow-2xl p-8 bg-zinc-900"
            onClick={(event) => event.stopPropagation()}
            draggable={false}
          />

          <div className="mt-4 text-zinc-400 text-sm font-medium select-none z-10">
            {lightboxIndex + 1} / {gigImages.length}
          </div>

          {lightboxIndex < gigImages.length - 1 && (
            <button
              className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(event) => {
                event.stopPropagation();
                setLightboxIndex((prev) => (prev !== null ? prev + 1 : prev));
              }}
            >
              <FiChevronRight size={24} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
